import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NotesApiAdapter } from '../adapters/NotesApiAdapter';
import { CreateNoteRequest, UpdateNoteRequest } from '../models/Note';

export const useNotes = (orderId?: number) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new NotesApiAdapter(httpClient);
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['crm-notes', orderId],
    queryFn: ({ pageParam = 1 }) => {
      return apiAdapter.getNotesPaginated(orderId, pageParam);
    },
    getNextPageParam: (lastPage) => lastPage?.nextPage ?? undefined,
    initialPageParam: 1,
    enabled: orderId !== undefined,
    staleTime: 1 * 60 * 1000, // 1 minuto
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  const notes = data?.pages.flatMap((page) => page?.notes ?? []) ?? [];
  const total = data?.pages?.[0]?.total ?? 0;

  const createMutation = useMutation({
    mutationFn: (data: CreateNoteRequest) => apiAdapter.createNote(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-notes', orderId] });
      queryClient.invalidateQueries({ queryKey: ['crm-orders'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ noteId, data }: { noteId: number; data: UpdateNoteRequest }) =>
      apiAdapter.updateNote(noteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-notes', orderId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId: number) => apiAdapter.deleteNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-notes', orderId] });
      queryClient.invalidateQueries({ queryKey: ['crm-orders'] });
    },
  });

  return {
    notes,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    createNote: createMutation.mutateAsync,
    updateNote: updateMutation.mutateAsync,
    deleteNote: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
